import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography, Chip, Divider } from "@mui/material";
import RecordCard from "./RecordCard";
import { localizeDateTimeShort } from "utils/dateFormats";
import { useGetShipmentRefundDetailQuery } from "store/services/PaymentsApi/shipmentRefundApi";

const statusStyles = {
    Approved: { backgroundColor: "#EBF5FB", textColor: "green" },
    Pending: { backgroundColor: "#FEF9E7", textColor: "#D4AC0D" },
    Rejected: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
    default: { backgroundColor: "#E9ECEF", textColor: "#6C757D" },
};
const getStatusStyle = (status) => statusStyles[status] || statusStyles.default;
const formatCurrency = (value) => {
    if (value == null || Number.isNaN(Number(value))) return "N/A";
    return `Rs. ${Number(value).toFixed(2)}`;
};

const FieldRow = ({ label, value }) => (
    <Box sx={{ display: "flex", py: 0.25 }}>
        <Box sx={{ width: "40%", color: "text.primary", fontSize: "0.7rem", fontWeight: 600 }}>{label}</Box>
        <Box sx={{ width: "60%", fontSize: "0.74rem", fontWeight: 500 }}>{value ?? "N/A"}</Box>
    </Box>
);

const ShipmentRefundCompact = ({ shipmentId }) => {
    const { data: shipmentRefundDetail, isLoading, isFetching } = useGetShipmentRefundDetailQuery(shipmentId, {
        skip: !shipmentId,
    });

    if (isLoading || isFetching || !shipmentRefundDetail) return null;

    const shipmentTotalCharge = Array.isArray(shipmentRefundDetail?.shipmentPrice)
        ? shipmentRefundDetail.shipmentPrice.reduce((sum, price) => sum + (Number(price?.charge) || 0), 0)
        : 0;
    const financeStatus = shipmentRefundDetail?.financeReview?.status;
    const isApproved = financeStatus === "Approved";
    const isRejected = financeStatus === "Rejected";
    const financeActor = isApproved
        ? shipmentRefundDetail?.financeReview?.approvedBy?.name
        : shipmentRefundDetail?.financeReview?.rejectedBy?.name;

    return (
        <Box sx={{ p: 1 }}>
            <RecordCard>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
                        {shipmentRefundDetail?._id ? (
                            <Link
                                to={`/app/crm/shipment-refund/${shipmentRefundDetail._id}/detail`}
                                style={{ color: "inherit", textDecoration: "underline" }}
                            >
                                {shipmentRefundDetail?.refundId || "N/A"}
                            </Link>
                        ) : "N/A"}
                    </Typography>
                    <Chip
                        label={shipmentRefundDetail?.refundStatus || "Unknown"}
                        size="small"
                        sx={{
                            backgroundColor: getStatusStyle(shipmentRefundDetail?.refundStatus).backgroundColor,
                            color: getStatusStyle(shipmentRefundDetail?.refundStatus).textColor,
                            fontWeight: 700,
                            textTransform: "uppercase",
                        }}
                    />
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <FieldRow label="Shipment" value={shipmentRefundDetail?.shipment?.shipmentId} />
                <FieldRow label="Total Charge" value={formatCurrency(shipmentTotalCharge)} />
                <FieldRow label="Refund Amount" value={formatCurrency(shipmentRefundDetail?.amount)} />
                <FieldRow label="Refund Type" value={shipmentRefundDetail?.refundType?.name} />
                <FieldRow label="Refund Method" value={shipmentRefundDetail?.refundMethod} />
                <FieldRow
                    label="Refund Date"
                    value={shipmentRefundDetail?.refundDate ? localizeDateTimeShort(shipmentRefundDetail.refundDate) : "N/A"}
                />
                <FieldRow
                    label={isApproved ? "Approved By" : isRejected ? "Rejected By" : "Reviewed By"}
                    value={financeActor}
                />
                <FieldRow label="Finance Payment Method" value={shipmentRefundDetail?.financeReview?.paymentMethod?.name} />
                <FieldRow
                    label="Finance Approved Date"
                    value={
                        shipmentRefundDetail?.financeReview?.paymentDate
                            ? localizeDateTimeShort(shipmentRefundDetail.financeReview.paymentDate)
                            : "N/A"
                    }
                />
                {shipmentRefundDetail?.financeReview?.bankName?.name && (
                    <FieldRow label="Bank Name" value={shipmentRefundDetail.financeReview.bankName.name} />
                )}
                {shipmentRefundDetail?.refundReason && (
                    <FieldRow label="Refund Reason" value={shipmentRefundDetail.refundReason} />
                )}
                {shipmentRefundDetail?.rejectReason && (
                    <FieldRow label="Reject Reason" value={shipmentRefundDetail.rejectReason} />
                )}
                {shipmentRefundDetail?.financeReview?.remarks && (
                    <FieldRow label="Finance Remarks" value={shipmentRefundDetail.financeReview.remarks} />
                )}
            </RecordCard>
        </Box>
    );
};

export default ShipmentRefundCompact;